import { AdjustmentsService, TcoService } from '@amzn/awscat-portfolio-assessment-service-client';
import { AwsAdjustment, CreateManualAdjustment, OnPremAdjustment } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Box, Button, Container, Grid, SpaceBetween } from '@amzn/awsui-components-react';
import { createRef, useContext, useEffect, useState } from 'react';
import { match, useHistory, useRouteMatch } from 'react-router';

import { HelpPanelTopicOptions } from '../../../common/Constants';
import { constants } from '../../../common/Localization';
import { ErrorWithResponse, MatchParam } from '../../../common/types';
import { HelpPanelContext } from '../../../contexts/HelpPanelContext';
import ConfirmationModal from '../../common/ConfirmationModal';
import AddEditAdjustmentItem from './AddEditAdjustmentItem';
import { AdjustmentType, ForwardRefFnType } from './interfaces';

const adjustmentsService = new AdjustmentsService();
const tcoService = new TcoService();

function AddAdjustment({ flash }: FlashContextInterface): JSX.Element {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/tco/:tcoAssessmentId',
    });

    const tcoAssessmentId = match?.params.tcoAssessmentId ?? null;
    const portfolioId = match?.params.portfolioId ?? null;

    const history = useHistory();

    const [ showCancelFriction, setShowCancelFriction ] = useState(false);
    const [ saveLoading, setSaveLoading ] = useState(false);
    const [ currency, setCurrency ] = useState('');
   
    const adjustmentsPage = `/portfolio/${portfolioId}/tco/${tcoAssessmentId}/adjustments`;
    
    const awsRef = createRef<ForwardRefFnType>();
    const onPremRef = createRef<ForwardRefFnType>();
    const { panelHandler } = useContext(HelpPanelContext);
    useEffect(() => {
        panelHandler(HelpPanelTopicOptions.ADJUSTMENTS_ADD);
    }, [ panelHandler ]);
    
    useEffect(() => {
        const loadCurrency = async () => {
            if (!portfolioId || !tcoAssessmentId || tcoAssessmentId === 'default') {
                return;
            }
            const tcoSummary = await tcoService.getSummary(tcoAssessmentId);
            setCurrency(tcoSummary.output.summary.currency);
        };
        loadCurrency();
    }, [ portfolioId, tcoAssessmentId ]);
    
    const create = async (): Promise<void> => {
        if (!portfolioId || !tcoAssessmentId || tcoAssessmentId === 'default') {
            return;
        }

        const awsValid = await awsRef.current?.validate();
        const onPremValid = await onPremRef.current?.validate();

        if (!awsValid || !onPremValid) {
            return;
        }

        const awsAdjustment = awsRef.current?.getAdjustment();
        const onPremAdjustment = onPremRef.current?.getAdjustment();

        if (!awsAdjustment && !onPremAdjustment) {
            return;
        }

        const newAdjustment: CreateManualAdjustment = {
            portfolioId, tcoAssessmentId
        };
        if (awsAdjustment) {
            newAdjustment.aws = awsAdjustment as AwsAdjustment;
        }
        if (onPremAdjustment) {
            newAdjustment.onPremises = onPremAdjustment as OnPremAdjustment;
        }

        setSaveLoading(true);
        try {
            await adjustmentsService.create(newAdjustment);
            flash.alert(FlashType.success, constants.SUCCESS_CREATE_ADJUSTMENT, '');
            history.push(adjustmentsPage);
        } catch (err) {
            flash.alert(FlashType.error, (err as ErrorWithResponse).message, constants.ERROR_CREATE_ADJUSTMENT);
        } finally {
            setSaveLoading(false);
        }
    };

    const cancel = (): void => {
        history.push(adjustmentsPage);
    };

    const onCancelClick = (): void => {
        const awsAdjustment = awsRef.current?.getAdjustment();
        const onPremAdjustment = onPremRef.current?.getAdjustment();

        if (!awsAdjustment && !onPremAdjustment) {
            cancel();
        } else {
            setShowCancelFriction(true);
        }
    };

    return (
        <Container>
            <SpaceBetween size='m'>
                <Grid gridDefinition={[{colspan: 6}, {colspan: 6}]}>
                    <AddEditAdjustmentItem
                        ref={onPremRef}
                        type={AdjustmentType.ON_PREMISES}
                        currency={currency}
                    />
                    <AddEditAdjustmentItem
                        ref={awsRef}
                        type={AdjustmentType.AWS}
                        currency={currency}
                    />
                </Grid>
                <Box float='right'>
                    <SpaceBetween direction='horizontal' size='m'>
                        <Button
                            data-testid='cancelBtn'
                            onClick={onCancelClick}
                            disabled={saveLoading}
                        >
                            {constants.CANCEL_BUTTON_TEXT}
                        </Button>
                        <Button
                            data-testid='createBtn'
                            variant='primary'
                            onClick={create}
                            loading={saveLoading}
                        >
                            {constants.SAVE_BUTTON_TEXT}
                        </Button>
                    </SpaceBetween>
                </Box>
            </SpaceBetween>
            { showCancelFriction ? (
                <ConfirmationModal
                    confirmButton={constants.CONTINUE_BUTTON_TEXT}
                    onConfirm={cancel}
                    onDismiss={() => { setShowCancelFriction(false); }}
                    header={constants.UNSAVED_CHANGES_WILL_BE_LOST}
                    visible
                >
                    {constants.UNSAVED_CHANGES_WILL_BE_LOST_CONTINUE}
                </ConfirmationModal>
            ) : null }
        </Container>
    );
}

export default withFlashContext(AddAdjustment);
